import { clone } from "lodash";
import React from "react";

const PincodeModalContext = React.createContext(null);

export const PincodeModalProvider = ({
  initial = {
    startScenario: false,
    startAssistanceRequest: false,
    openAlarms: false,
  },
  children,
}) => {
  const [tilesLocked, setTilesLocked] = React.useState(initial);

  const activePincode = React.useRef(null);
  const timersRef = React.useRef(new Map());
  const [isPincodeOpen, setIsPincodeOpen] = React.useState(false);
  const [tempUnlockList, setTempUnlockList] = React.useState([]);

  const updateTilesLocked = React.useCallback(
    (type, lockState) => {
      if (!type) return;
      let currentTilesLocked = clone(tilesLocked);

      currentTilesLocked[type] = lockState;

      setTilesLocked(currentTilesLocked);
    },
    [tilesLocked]
  );

  const openPincodeModal = React.useCallback((pincode, callback) => {
    activePincode.current = {
      pincode,
      callback,
    };
    setIsPincodeOpen(true);
  }, []);

  const closePincodeModal = React.useCallback(() => {
    activePincode.current = null;
    setIsPincodeOpen(false);
  }, []);

  const removeTempUnlock = React.useCallback((type) => {
    if (!type) return;

    const existing = timersRef.current.get(type);
    if (existing) {
      clearTimeout(existing);
      timersRef.current.delete(type);
    }

    setTempUnlockList((prev) => prev.filter((t) => t !== type));
  }, []);

  const clearAllTimers = React.useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current.clear();
  }, []);

  const tempUnlock = React.useCallback(
    (type, ms = 60 * 60 * 1000) => {
      if (!type) return;

      setTempUnlockList((prev) =>
        prev.includes(type) ? prev : [...prev, type]
      );

      const existing = timersRef.current.get(type);
      if (existing) {
        clearTimeout(existing);
        timersRef.current.delete(type);
      }

      const id = setTimeout(() => {
        removeTempUnlock(type);
      }, ms);
      timersRef.current.set(type, id);
    },
    [removeTempUnlock]
  );

  React.useEffect(() => clearAllTimers, [clearAllTimers]);
  React.useEffect(() => {}, [tempUnlockList?.length]);

  const value = React.useMemo(
    () => ({
      tilesLocked,
      updateTilesLocked,
      openPincodeModal,
      closePincodeModal,
      activePincode,
      tempUnlock,
      tempUnlockList,
      setTempUnlockList,
      isPincodeOpen,
    }),
    [
      tilesLocked,
      updateTilesLocked,
      openPincodeModal,
      closePincodeModal,
      activePincode,
      tempUnlock,
      isPincodeOpen,
      tempUnlockList,
      setTempUnlockList,
    ]
  );

  return (
    <PincodeModalContext.Provider value={value}>
      {children}
    </PincodeModalContext.Provider>
  );
};

export const usePincodeModal = () => {
  const context = React.useContext(PincodeModalContext);
  if (!context) {
    throw new Error(
      "usePincodeModal must be used within a PincodeModalProvider"
    );
  }
  return context;
};
